import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Output, EventEmitter, HostListener, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { Product } from '../../../models/product.model';
import { ProductService } from '../../../services/product.service';

@Component({
  selector: 'app-product-list',
  imports: [
    CommonModule
  ],
  templateUrl: './product-list.component.html'
})
export class ProductListComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() productSelected = new EventEmitter<{product: Product, amount: number}>();
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  products: Product[] = [];
  selectedProductIndex: number = -1;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  currentPage: number = 1;
  totalRecords: number = 0;
  totalPages: number = 0;
  hasMorePages: boolean = true;
  private scrollThreshold: number = 200; // Carregar mais quando estiver a 200px do fim

  constructor(
    private productService: ProductService
  ) {}

  ngOnInit(): void {
    this.loadProducts();
  }

  ngAfterViewInit(): void {
    // Adicionar listener de scroll após a view ser inicializada
    setTimeout(() => {
      this.setupScrollListener();
    }, 100);
  }

  ngOnDestroy(): void {
    // Cleanup se necessário
  }

  private setupScrollListener(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.addEventListener('scroll', () => this.onScroll());
    }
  }

  private onScroll(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container || this.isLoadingMore || !this.hasMorePages) {
      return;
    }

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Carregar mais produtos quando estiver próximo do fim
    if (distanceFromBottom < this.scrollThreshold) {
      this.loadMoreProducts();
    }
  }

  /**
   * Captura eventos de teclado globalmente para navegação na lista de produtos
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Verificar se está em input ou textarea
    const target = event.target as HTMLElement;
    const isInputFocused = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
    
    // Se houver produtos na lista, permitir navegação com setas
    if(this.products.length > 0 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Permitir navegação quando não está digitando
      if(!isInputFocused) {
        event.preventDefault();
        if(event.key === "ArrowDown") {
          this.navigateDown();
        } else if(event.key === "ArrowUp") {
          this.navigateUp();
        }
      }
    }

    // Enter para selecionar produto
    if(event.key === "Enter" && this.products.length > 0 && this.selectedProductIndex >= 0) {
      if(!isInputFocused) {
        event.preventDefault();
        this.selectProduct();
      }
    }
  }

  /**
   * Método público para ser chamado pelo componente pai para carregar produtos
   */
  loadProducts(): void {
    this.isLoading = true;
    this.currentPage = 1;
    this.products = [];
    this.hasMorePages = true;
    // Buscar todos os produtos ativos sem filtro de busca
    this.productService.getAll('', true, false, false, this.currentPage, true).subscribe({
      next: (response) => {
        this.products = response.data || [];
        this.totalRecords = response.pagination?.totalRecords || 0;
        this.totalPages = response.pagination?.totalPages || 0;
        this.selectedProductIndex = this.products.length > 0 ? 0 : -1;
        this.isLoading = false;
        // Verificar se há mais páginas
        this.checkHasMorePages();
      },
      error: (err) => {
        this.products = [];
        this.selectedProductIndex = -1;
        this.isLoading = false;
        this.hasMorePages = false;
      }
    });
  }

  private loadMoreProducts(): void {
    if (this.isLoadingMore || !this.hasMorePages) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;

    this.productService.getAll('', true, false, false, this.currentPage, true).subscribe({
      next: (response) => {
        const newProducts = response.data || [];
        // Adicionar novos produtos aos existentes
        this.products = [...this.products, ...newProducts];
        this.totalPages = response.pagination?.totalPages || 0;
        this.isLoadingMore = false;
        // Verificar se há mais páginas
        this.checkHasMorePages();
      },
      error: (err) => {
        this.isLoadingMore = false;
        this.hasMorePages = false;
      }
    });
  }

  private checkHasMorePages(): void {
    // Verificar se há mais produtos para carregar
    this.hasMorePages = this.currentPage < this.totalPages;
  }

  selectProductFromList(product: Product): void {
    this.productSelected.emit({product, amount: 1});
  }

  private selectProduct(): void {
    if(this.selectedProductIndex >= 0 && this.selectedProductIndex < this.products.length) {
      const selectedProduct = this.products[this.selectedProductIndex];
      this.productSelected.emit({product: selectedProduct, amount: 1});
    }
  }

  private navigateDown(): void {
    if(this.products.length > 0){
      this.selectedProductIndex = Math.min(this.selectedProductIndex + 1, this.products.length - 1);
      this.scrollToSelected();
    }
  }

  private navigateUp(): void {
    if(this.products.length > 0){
      this.selectedProductIndex = Math.max(this.selectedProductIndex - 1, -1);
      this.scrollToSelected();
    }
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const element = document.getElementById(`product-item-${this.selectedProductIndex}`);
      if(element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  }
}

